import { useCallback, useMemo, useState } from 'react';
import { Calendar, Badge, List, Typography, Empty, Tag } from 'antd';
import dayjs from 'dayjs';
import { EntityImage } from '../Entities/EntityImage';
import './BrowseCalendar.css';

const { Text } = Typography;

// How many names a day cell shows before folding the rest into "+N more".
const CELL_LIMIT = 3;

/** An entity with a start or an end — an occasion (todo D12). */
export const isDated = e => !!(e.starts_at || e.ends_at);

/**
 * Whether `entity`'s window puts it on `day`. A bounded window covers every
 * day from its start to its end, like an all-day event that spans. A window
 * with one open end is shown on the one day it names: an open-ended group is
 * "live" on every later day, and listing it in every cell would bury the
 * occasions that actually begin or end that month. The hangout calendar feed
 * makes the same call, placing an occasion on the day it starts.
 */
export function onDay(entity, day) {
    const from = entity.starts_at ? dayjs(entity.starts_at) : null;
    const until = entity.ends_at ? dayjs(entity.ends_at) : null;
    if (!from && !until) return false;
    if (from && until) return !day.isBefore(from, 'day') && !day.isAfter(until, 'day');
    return day.isSame(from || until, 'day');
}

/** "5 Nov 2026 – 20 Dec 2026", "From 5 Nov 2026" or "Until 20 Dec 2026". */
export function windowLabel(entity) {
    const f = d => dayjs(d).format('D MMM YYYY');
    if (entity.starts_at && entity.ends_at) return `${f(entity.starts_at)} – ${f(entity.ends_at)}`;
    if (entity.starts_at) return `From ${f(entity.starts_at)}`;
    return `Until ${f(entity.ends_at)}`;
}

// Earliest start first; an end-only window sorts by its end. Ties by name.
const byWindow = (a, b) => {
    const ka = dayjs(a.starts_at || a.ends_at).valueOf();
    const kb = dayjs(b.starts_at || b.ends_at).valueOf();
    return ka - kb || (a.name || '').localeCompare(b.name || '');
};

/**
 * The browse page's calendar view: the rows the table would show, laid over a
 * month by their windows. Selecting a day lists what is on it, each entry
 * opening its entity. Rows with no window are counted below the calendar
 * rather than drawn, since a person or an ongoing group has no day to sit on.
 *
 * @param entities            the filtered browse rows (same set as the table)
 * @param onOpen              called with the entity a list entry was chosen for
 * @param showTypeIdentifiers colour the dots by type and tag each entry
 * @param typeColors          { [entity_type]: colour } when identifiers are on
 */
function BrowseCalendar({ entities, onOpen, showTypeIdentifiers = false, typeColors = {} }) {
    const [selectedDate, setSelectedDate] = useState(() => dayjs());

    const dated = useMemo(() => entities.filter(isDated).sort(byWindow), [entities]);
    const undatedCount = entities.length - dated.length;
    const forDay = useCallback(day => dated.filter(e => onDay(e, day)), [dated]);

    const colorOf = e => (showTypeIdentifiers ? (typeColors[e.entity_type] || 'blue') : 'blue');

    if (dated.length === 0) {
        return (
            <Empty
                description={(
                    <Text type="secondary">
                        Nothing here has dates yet — {undatedCount === 1 ? 'the one entity' : `the ${undatedCount} entities`} for
                        this filter {undatedCount === 1 ? 'is' : 'are'} only in the table view.
                        Give a context or relationship a <strong>When</strong> and it shows up here.
                    </Text>
                )}
            />
        );
    }

    const cellRender = (current, info) => {
        if (info.type !== 'date') return info.originNode;
        const items = forDay(current);
        if (items.length === 0) return info.originNode;
        return (
            <ul className="browse-calendar-cell">
                {items.slice(0, CELL_LIMIT).map(e => (
                    <li key={e.id}><Badge color={colorOf(e)} text={e.name} /></li>
                ))}
                {items.length > CELL_LIMIT && (
                    <li><Text type="secondary" style={{ fontSize: 12 }}>+{items.length - CELL_LIMIT} more</Text></li>
                )}
            </ul>
        );
    };

    const dayItems = forDay(selectedDate);

    return (
        <div className="browse-calendar">
            <Calendar cellRender={cellRender} onSelect={setSelectedDate} />
            <div className="browse-calendar-day">
                <Text strong>{selectedDate.format('dddd D MMMM YYYY')}</Text>
                {dayItems.length === 0 ? (
                    <Empty image={Empty.PRESENTED_IMAGE_SIMPLE} description={<Text type="secondary">Nothing on this day</Text>} />
                ) : (
                    <List
                        dataSource={dayItems}
                        renderItem={e => (
                            <List.Item className="browse-calendar-item" onClick={() => onOpen(e)}>
                                <List.Item.Meta
                                    avatar={<EntityImage entity={e} size={28} />}
                                    title={e.name}
                                    description={windowLabel(e)}
                                />
                                {showTypeIdentifiers && (
                                    <Tag style={{ textTransform: 'capitalize', margin: 0 }}>{e.entity_type}</Tag>
                                )}
                            </List.Item>
                        )}
                    />
                )}
            </div>
            {undatedCount > 0 && (
                <Text type="secondary" style={{ fontSize: 12 }}>
                    {undatedCount} without dates — only in the table view.
                </Text>
            )}
        </div>
    );
}

export default BrowseCalendar;
